import { Link } from 'react-router-dom';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

const pillars = [
  {
    icon: 'ri-radar-line',
    number: '01',
    title: 'Intelligent Fall Detection',
    description: 'Diitect\'s core technology identifies fall events with precision, enabling faster awareness and reducing the window between incident and response.'
  },
  {
    icon: 'ri-eye-line',
    number: '02',
    title: 'Continuous Monitoring',
    description: 'Passive, always-on monitoring across care environments provides persistent visibility without disrupting daily routines or requiring active engagement.'
  },
  {
    icon: 'ri-alarm-warning-line',
    number: '03',
    title: 'Faster Response',
    description: 'Automated alerts and event notifications reach the right people immediately, compressing response times and improving intervention outcomes.'
  },
  {
    icon: 'ri-shield-check-line',
    number: '04',
    title: 'Risk Mitigation',
    description: 'By surfacing patterns and early indicators, Diitect helps care teams proactively manage risk before critical events occur.'
  }
];

const outcomes = [
  {
    value: 'Faster',
    label: 'Incident Detection',
    description: 'Compressed time between event and awareness'
  },
  {
    value: 'Stronger',
    label: 'Risk Mitigation',
    description: 'Proactive visibility across care environments'
  },
  {
    value: 'Better',
    label: 'Care Outcomes',
    description: 'Timely intervention when it matters most'
  }
];

const whyMatters = [
  {
    icon: 'ri-time-line',
    title: 'Delayed Response Has Consequences',
    description: 'Falls and critical events in care settings can escalate rapidly. Every minute of delay increases risk. Diitect is built to close that gap.'
  },
  {
    icon: 'ri-building-4-line',
    title: 'Designed for Real Environments',
    description: 'Diitect\'s technology is practical and deployment-ready — built for the realities of care facilities, not controlled lab conditions.'
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Outcome-Oriented Approach',
    description: 'Every design decision is anchored to measurable outcomes: faster detection, stronger safety, and better care.'
  }
];

export default function DiitectPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Diitect',
        item: `${siteUrl}/diitect`
      }
    ]
  };

  const diitectSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Diitect | Intelligent Fall Detection & Safety Technology | Diivy Portfolio',
    url: `${siteUrl}/diitect`,
    description: 'Diitect develops intelligent fall detection and monitoring technology for faster intervention, stronger risk mitigation, and better outcomes across care environments.',
    about: {
      '@type': 'SoftwareApplication',
      name: 'Diitect',
      applicationCategory: 'HealthApplication',
      description: 'Intelligent fall detection and monitoring technology for care environments',
      featureList: [
        'Intelligent Fall Detection',
        'Continuous Monitoring',
        'Faster Response',
        'Risk Mitigation'
      ]
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'Diivy',
      url: siteUrl,
      description: 'Investment and technology portfolio company focused on health technology, safety, and commerce'
    }
  };

  return (
    <>
      <SEOHead
        title="Diitect | Intelligent Fall Detection & Safety Technology | Diivy Portfolio"
        description="Diitect develops intelligent fall detection and monitoring technology for faster intervention, stronger risk mitigation, and better outcomes across care environments."
        keywords="fall detection, medical safety technology, care monitoring, intelligent detection, patient safety, Diitect"
        canonical="/diitect"
        ogTitle="Diitect | Intelligent Fall Detection & Safety Technology"
        ogDescription="Medical and safety technology for faster detection and response. Diitect builds intelligent monitoring solutions for care environments."
        ogUrl="/diitect"
        twitterTitle="Diitect | Intelligent Fall Detection & Safety Technology"
        twitterDescription="Medical and safety technology for faster detection and response across care environments."
      />
      <JsonLd data={diitectSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-[88px] bg-[#0A0F1E] overflow-hidden">
          {/* Subtle grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px), repeating-linear-gradient(90deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px)'
            }}
          ></div>

          {/* Background image with overlay */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/58fc71e3162e828bcde20de64e577d9d.jpg"
              alt="Diitect care environment"
              className="w-full h-full object-cover object-top opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E] via-[#0A0F1E]/90 to-[#0A0F1E]/70"></div>
          </div>

          <div className="relative max-w-[1200px] mx-auto px-12 pt-24 pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              {/* Left — Content */}
              <div className="space-y-10">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Portfolio Company
                  </p>
                </div>

                <div className="space-y-5">
                  <h1 className="text-5xl lg:text-6xl font-light text-white leading-[1.1] tracking-[-0.02em]">
                    Medical &amp; Safety<br />
                    <span className="font-semibold">Technology</span>
                  </h1>
                  <p className="text-[17px] text-[#C9A96E] font-light tracking-wide">
                    Faster detection. Faster response.
                  </p>
                </div>

                <p className="text-[15px] leading-[1.9] text-white/60 font-light max-w-[480px]">
                  Diitect develops intelligent fall detection and monitoring technology designed to support faster intervention, stronger risk mitigation, and better outcomes across care environments.
                </p>

                <div className="flex items-center gap-5 pt-2">
                  <a
                    href="https://diitect.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[13px] font-semibold text-[#0A0F1E] rounded-sm hover:bg-[#C9A96E]/90 transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer"
                  >
                    Visit Diitect.com
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-3 px-8 py-3.5 border border-[#C9A96E]/40 text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] hover:border-[#C9A96E] transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer"
                  >
                    Investor Inquiry
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>

              {/* Right — Visual card */}
              <div className="relative">
                <div className="relative rounded-sm overflow-hidden border border-white/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=close-up%20dramatic%20low%20angle%20perspective%20looking%20up%20at%20modern%20intelligent%20fall%20detection%20sensor%20device%20dome%20shaped%20circular%20white%20sensor%20mounted%20on%20ceiling%20in%20clean%20clinical%20environment%2C%20center%20spotlight%20against%20dark%20navy%20edges%2C%20sophisticated%20medical%20safety%20monitoring%20equipment%20product%20photography%2C%20no%20people%2C%20high%20contrast%20dark%20background%20with%20white%20device%20in%20center%2C%20minimal%20and%20precise%2C%20gold%20light%20accent%20around%20edge%20of%20dome%20device&width=600&height=480&seq=diitect-hero-sensor-v2&orientation=landscape"
                    alt="Diitect intelligent monitoring technology"
                    className="w-full h-[380px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/60 via-transparent to-transparent"></div>

                  {/* Floating label */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-sm px-5 py-4">
                      <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#C9A96E] mb-1">Diitect</p>
                      <p className="text-[13px] text-white/80 font-light">Intelligent Fall Detection &amp; Monitoring</p>
                    </div>
                  </div>
                </div>

                {/* Decorative corner */}
                <div className="absolute -top-3 -right-3 w-16 h-16 border-t-2 border-r-2 border-[#C9A96E]/30"></div>
                <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b-2 border-l-2 border-[#C9A96E]/30"></div>
              </div>
            </div>

            {/* Outcome stats row */}
            <div className="grid grid-cols-3 gap-6 mt-20 pt-16 border-t border-white/5">
              {outcomes.map((item, i) => (
                <div
                  key={i}
                  className="text-center py-8 border border-white/5 rounded-sm bg-white/[0.03] hover:border-[#C9A96E]/30 hover:bg-white/[0.05] transition-all duration-500"
                >
                  <p className="text-4xl font-bold text-[#C9A96E] tracking-[-0.02em]">{item.value}</p>
                  <p className="mt-2 text-[13px] font-semibold text-white/70 tracking-wide">{item.label}</p>
                  <p className="mt-1 text-[12px] text-white/35 font-light">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-32 bg-white">
          <div className="max-w-[1080px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
              {/* Left label */}
              <div className="lg:col-span-4 space-y-6 pt-2">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Overview
                  </p>
                </div>
                <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em]">
                  Built for<br />
                  <span className="font-semibold">Care Settings</span>
                </h2>
                <div className="w-full h-[1px] bg-gradient-to-r from-[#C9A96E] to-transparent mt-6"></div>
              </div>

              {/* Right body */}
              <div className="lg:col-span-8 space-y-7 text-[16px] text-gray-600 leading-[1.9] font-light">
                <p>
                  Diitect is focused on technology that helps detect critical events quickly and support timely action. Its solutions are built for settings where faster awareness can improve safety, strengthen operations, and lead to better outcomes.
                </p>
                <p>
                  Falls and delayed response can create serious consequences across care settings. Diitect addresses this challenge with technology designed to improve visibility, reduce delay, and support faster intervention when it matters most.
                </p>
                <p>
                  Its approach is practical, outcome-oriented, and designed for real-world care environments — not theoretical frameworks. Every element of the platform is built around the question: what actually helps care teams respond faster?
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Diitect Does — 4 Pillars */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[1200px] mx-auto px-12">
            <div className="text-center space-y-6 mb-20">
              <div className="flex items-center justify-center gap-5">
                <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                  What Diitect Does
                </p>
                <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              </div>
              <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[560px] mx-auto">
                Intelligent Monitoring<br />
                <span className="font-semibold">Centered on Response</span>
              </h2>
              <p className="text-[15px] leading-[1.8] text-gray-500 font-light max-w-[540px] mx-auto">
                Diitect builds intelligent monitoring technology centered on fall detection, event awareness, and faster response.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pillars.map((pillar, i) => (
                <div
                  key={i}
                  className="group p-10 bg-white border border-[#0A0F1E]/6 rounded-sm hover:border-[#C9A96E]/40 hover:shadow-sm transition-all duration-400"
                >
                  <div className="flex items-start gap-8">
                    <div className="flex-shrink-0 space-y-4">
                      <span className="block text-[11px] font-semibold tracking-[0.2em] text-[#C9A96E]/50">
                        {pillar.number}
                      </span>
                      <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-[#C9A96E]/10 group-hover:bg-[#C9A96E]/20 transition-colors duration-300">
                        <i className={`${pillar.icon} text-xl text-[#C9A96E]`}></i>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-[17px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                        {pillar.title}
                      </h3>
                      <p className="text-[14px] leading-[1.8] text-gray-500 font-light">
                        {pillar.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="py-32 bg-white">
          <div className="max-w-[1200px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
              {/* Left */}
              <div className="lg:col-span-4 space-y-6 pt-2">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Why It Matters
                  </p>
                </div>
                <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em]">
                  The Problem<br />
                  <span className="font-semibold">Worth Solving</span>
                </h2>
                <p className="text-[15px] leading-[1.8] text-gray-500 font-light">
                  Falls and delayed response create serious, measurable consequences across care settings. Diitect exists to address this directly.
                </p>
                <div className="w-full h-[1px] bg-gradient-to-r from-[#C9A96E] to-transparent mt-4"></div>
              </div>

              {/* Right — 3 cards */}
              <div className="lg:col-span-8 space-y-5">
                {whyMatters.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-7 p-8 border border-[#0A0F1E]/6 rounded-sm hover:border-[#C9A96E]/30 hover:bg-[#F7F4EF]/50 transition-all duration-400 group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-sm bg-[#C9A96E]/10 group-hover:bg-[#C9A96E]/20 transition-colors duration-300">
                      <i className={`${item.icon} text-xl text-[#C9A96E]`}></i>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-[16px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                        {item.title}
                      </h3>
                      <p className="text-[14px] leading-[1.8] text-gray-500 font-light">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Within the Diivy Portfolio — Pull Quote Section */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[1080px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
              {/* Left — image */}
              <div className="lg:col-span-5 relative">
                <div className="rounded-sm overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20portfolio%20investment%20concept%2C%20interconnected%20nodes%20and%20lines%2C%20dark%20navy%20background%20with%20warm%20gold%20accent%20elements%2C%20sophisticated%20financial%20technology%20visualization%2C%20minimalist%20geometric%20design%2C%20no%20people%2C%20clean%20and%20elegant%20composition&width=560&height=480&seq=diitect-portfolio-01&orientation=portrait"
                    alt="Diivy portfolio"
                    className="w-full h-[400px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/30 to-transparent rounded-sm"></div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-[#C9A96E]/30"></div>
              </div>

              {/* Right — text */}
              <div className="lg:col-span-7 space-y-8">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Within the Diivy Portfolio
                  </p>
                </div>

                <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em]">
                  Part of a Broader<br />
                  <span className="font-semibold">Strategic Focus</span>
                </h2>

                <p className="text-[15px] leading-[1.9] text-gray-600 font-light">
                  As part of the Diivy portfolio, Diitect reflects a broader focus on businesses in health technology, safety, and commerce that are built around meaningful problems and long-term value.
                </p>

                <p className="text-[15px] leading-[1.9] text-gray-600 font-light">
                  Diivy supports Diitect with strategic positioning, operational guidance, and a long-term commitment to building technology that matters — not just technology that exists.
                </p>

                {/* Pull quote */}
                <div className="relative mt-8 pl-8 border-l-2 border-[#C9A96E]/40">
                  <p className="text-[18px] font-light text-[#0A0F1E] leading-[1.6] italic tracking-[-0.01em]">
                    "Businesses built around meaningful problems and long-term value."
                  </p>
                </div>

                <div className="flex items-center gap-5 pt-4">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-3 text-[13px] font-semibold text-[#C9A96E] hover:text-[#0A0F1E] transition-colors duration-300 whitespace-nowrap tracking-wide cursor-pointer group"
                  >
                    About Diivy
                    <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300"></i>
                  </Link>
                  <span className="text-[#0A0F1E]/15">|</span>
                  <a
                    href="https://diitect.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-3 text-[13px] font-semibold text-[#C9A96E] hover:text-[#0A0F1E] transition-colors duration-300 whitespace-nowrap tracking-wide cursor-pointer group"
                  >
                    Visit Diitect.com
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="py-24 bg-[#0A0F1E] relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px), repeating-linear-gradient(90deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px)'
            }}
          ></div>
          <div className="relative max-w-[1200px] mx-auto px-12 flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-[640px]">
              <h3 className="text-3xl font-light text-white mb-5 leading-[1.4] tracking-[-0.01em]">
                Interested in Diitect or the Diivy portfolio?
              </h3>
              <p className="text-[15px] text-white/50 leading-relaxed font-light">
                We welcome conversations with institutional partners and investors focused on health technology and safety.
              </p>
            </div>
            <div className="flex items-center gap-5 flex-shrink-0">
              <a
                href="https://diitect.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[13px] font-semibold text-[#0A0F1E] rounded-sm hover:bg-[#C9A96E]/90 transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer"
              >
                Visit Diitect.com
                <i className="ri-external-link-line text-sm"></i>
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-3.5 border-2 border-[#C9A96E] text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer group"
              >
                Investor Inquiry
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300"></i>
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
